import * as Sentry from '@sentry/react'
import { getAddresses, updateSubscriptionAddress } from 'actions/Address'
import { getPaymentMethods } from 'actions/PaymentMethod'
import { attemptFetchStock } from 'actions/Products'
import { submit3DSecureChallengeForm } from 'actions/SecureChallenge'
import { fetchSubscription } from 'actions/Subscription'
import axios from 'axios'
import { push } from 'connected-react-router'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import snakeCase from 'lodash/snakeCase'
import { showNotification } from 'notification'
import { PaymentMethodType } from 'types/paymentMethodType'
import {
    hasAddress,
    hasCardToken,
    hasContactInfo,
    hasDeliveryDate,
    hasEmail,
    hasEnoughQuantity,
    hasPreferredPaymentMethod,
    hasProducts,
    hasSupportedAddress,
} from 'utilities/checkoutValidation'
import { getDateAsText, getDateYyyyMmDd } from 'utilities/date'
import i18n from 'utilities/i18n'
import { asSingleOrderProducts } from 'utilities/subscribe-api'

export function gotoCheckoutPage(checkoutMode) {
    return (dispatch) => {
        dispatch({ type: 'SET_CHECKOUT_MODE', payload: checkoutMode })
        dispatch(push('/checkout'))
        dispatch(beginCheckout())
    }
}

export function beginCheckout() {
    return (dispatch, getState) => {
        dispatch({ type: 'BEGIN_CHECKOUT', payload: getState().cart })
    }
}

function validateOrderData(data, dispatch, loggedIn, enoughQuantity, paymentMethodType) {
    let orderValid = true
    let totalIsZero = data.price === 0
    let paymentMethodTypeIsCreditCard = paymentMethodType === PaymentMethodType.CREDIT_CARD

    if (!hasContactInfo(data)) {
        dispatch({
            type: 'ORDER_VALIDATION_FAILED',
            payload: 'missingContactInfo',
        })
        orderValid = false
    }
    if (!hasDeliveryDate(data)) {
        dispatch({
            type: 'ORDER_VALIDATION_FAILED',
            payload: 'missingDeliveryDate',
        })
        orderValid = false
    }
    if (!hasEmail(data)) {
        dispatch({ type: 'ORDER_VALIDATION_FAILED', payload: 'missingEmail' })
        orderValid = false
    }
    if (!hasProducts(data)) {
        dispatch({ type: 'ORDER_VALIDATION_FAILED', payload: 'missingProducts' })
        orderValid = false
    }
    if (!hasAddress(data)) {
        dispatch({ type: 'ORDER_VALIDATION_FAILED', payload: 'missingAddress' })
        orderValid = false
    }
    if (!hasEnoughQuantity(enoughQuantity)) {
        dispatch({
            type: 'ORDER_VALIDATION_FAILED',
            payload: 'missingEnoughQuantity',
        })
        orderValid = false
    }
    if (!hasSupportedAddress(data)) {
        dispatch({
            type: 'ORDER_VALIDATION_FAILED',
            payload: 'unsupportedAddress',
        })
        orderValid = false
    }
    if (!loggedIn && paymentMethodTypeIsCreditCard) {
        if (!hasCardToken(data) && !totalIsZero) {
            dispatch({ type: 'ORDER_VALIDATION_FAILED', payload: 'missingCard' })
            orderValid = false
        }
    }

    return orderValid
}

function validateSubscription(data, dispatch, loggedIn) {
    if (!loggedIn) {
        dispatch({ type: 'ORDER_VALIDATION_FAILED', payload: 'notLoggedIn' })
        return false
    }

    let orderValid = true

    if (!hasContactInfo(data)) {
        dispatch({
            type: 'ORDER_VALIDATION_FAILED',
            payload: 'missingContactInfo',
        })
        orderValid = false
    }
    if (!hasEmail(data)) {
        dispatch({ type: 'ORDER_VALIDATION_FAILED', payload: 'missingEmail' })
        orderValid = false
    }
    if (!hasProducts(data)) {
        dispatch({ type: 'ORDER_VALIDATION_FAILED', payload: 'missingProducts' })
        orderValid = false
    }
    if (!hasAddress(data)) {
        dispatch({ type: 'ORDER_VALIDATION_FAILED', payload: 'missingAddress' })
        orderValid = false
    } else if (!hasSupportedAddress(data)) {
        dispatch({
            type: 'ORDER_VALIDATION_FAILED',
            payload: 'unsupportedAddress',
        })
        orderValid = false
    }
    if (!hasPreferredPaymentMethod(data)) {
        dispatch({ type: 'ORDER_VALIDATION_FAILED', payload: 'missingCard' })
        orderValid = false
    }

    return orderValid
}

// Maps order data according to previous model
// In case of gift:
//  gift: gift sender (personalInfo) instead of receiver
//  address: merged with gift receiver (gift)
function mapOrderData(
    products,
    price,
    address,
    deliveryDate,
    email,
    adyenCardToken,
    gift,
    optinForNews,
    personalInfo,
    paymentMethodId,
    paymentMethodType
) {
    if (gift.isGift) {
        return {
            giftData: {
                ...omit(gift, 'isGift'),
                firstName: personalInfo.firstName,
                lastName: personalInfo.lastName,
                mobile: personalInfo.mobile,
            },
            products: asSingleOrderProducts(products),
            price,
            deliveryDate,
            deliveryAddress: {
                ...address,
                firstName: gift.firstName,
                lastName: gift.lastName,
                mobile: gift.mobile,
            },
            optinForNews,
            couponCode: null,
            unauthenticatedOrderData: {
                email: email,
                shopId: process.env.SUBSCRIBE_SHOP_ID,
            },
            adyenCardToken:
                paymentMethodType === PaymentMethodType.CREDIT_CARD ? adyenCardToken : undefined, // Might not be set, if logged in user chooses stored payment method.
            email: email,
            paymentMethodId:
                paymentMethodType === PaymentMethodType.CREDIT_CARD ? paymentMethodId : undefined,
            paymentMethodType: paymentMethodType,
            redirectToBeta: process.env.IS_BETA,
        }
    } else {
        return {
            giftData: null,
            products: asSingleOrderProducts(products),
            price,
            deliveryDate,
            deliveryAddress: {
                ...address,
                firstName: personalInfo.firstName,
                lastName: personalInfo.lastName,
                mobile: personalInfo.mobile,
            },
            optinForNews,
            couponCode: null,
            unauthenticatedOrderData: {
                email: email,
                shopId: process.env.SUBSCRIBE_SHOP_ID,
            },
            adyenCardToken:
                paymentMethodType === PaymentMethodType.CREDIT_CARD ? adyenCardToken : undefined, // Might not be set, if logged in user chooses stored payment method.
            email: email,
            paymentMethodId:
                paymentMethodType === PaymentMethodType.CREDIT_CARD ? paymentMethodId : undefined,
            paymentMethodType: paymentMethodType,
            redirectToBeta: process.env.IS_BETA,
        }
    }
}

function mapSubscriptionData(subscription) {
    return subscription.products.map((p) => ({
        ...p,
        id: parseInt(p.id),
    }))
}

export function placeOrder(
    products,
    price,
    address,
    deliveryDate,
    email,
    adyenCardToken,
    gift,
    optinForNews,
    personalInfo,
    paymentMethodId,
    paymentMethodType
) {
    const url = `${process.env.CORE_API}/order`
    const data = mapOrderData(
        products,
        price,
        address,
        deliveryDate,
        email,
        adyenCardToken,
        gift,
        optinForNews,
        personalInfo,
        paymentMethodId,
        paymentMethodType
    )

    return (dispatch, getState) => {
        dispatch({ type: 'PLACE_ORDER' })

        if (getState().coupon.codeIsValid) {
            data.couponCode = getState().coupon.code
        }
        data.sessionId = getState().cart.id
        if (
            !validateOrderData(
                data,
                dispatch,
                getState().user.isLoggedIn,
                getState().cart.hasEnoughQuantity,
                paymentMethodType
            )
        )
            return
        axios
            .post(url, data)
            .then((response) => {
                // TODO: Clean up error handling. All errors should be in catch when OrderServiceV2 is live.
                if (response.data.status === 'payment-challenge') {
                    submit3DSecureChallengeForm(response)
                } else if (response.data.status === 'vipps-redirect') {
                    window.location.href = response.data.redirectUrl
                } else if (
                    response.data.status === 'failed' ||
                    response.data.errorReference != null
                ) {
                    if (response.data.errorReference) {
                        dispatch({
                            type: 'PLACE_ORDER_ERROR',
                            payload: [response.data],
                        })
                        Sentry.withScope((scope) => {
                            scope.setFingerprint([url, 'with reference'])
                            Sentry.captureMessage(
                                JSON.stringify(response.data),
                                Sentry.Severity.Error
                            )
                        })
                    } else {
                        dispatch({
                            type: 'PLACE_ORDER_ERROR',
                            payload: response.data.error,
                        })
                        if (response.data.error) {
                            response.data.error.forEach((e) => {
                                const type = snakeCase(e.type).toUpperCase()
                                dispatch({ type: `PLACE_ORDER_ERROR_${type}`, payload: e })
                                Sentry.withScope((scope) => {
                                    scope.setFingerprint([url, type])
                                    Sentry.captureMessage(
                                        JSON.stringify(response.data),
                                        Sentry.Severity.Error
                                    )
                                })
                            })
                        }
                    }
                } else {
                    dispatch({
                        type: 'PLACE_ORDER_SUCCESS',
                        payload: response,
                    })

                    const subscriptionAddress = getState().address.subscription
                    if (!subscriptionAddress || isEmpty(subscriptionAddress)) {
                        dispatch(getAddresses())
                    }

                    const preferredPaymentMethod = getState().paymentMethod.preferred
                    if (!preferredPaymentMethod || isEmpty(preferredPaymentMethod)) {
                        dispatch(getPaymentMethods())
                    }
                }
            })
            .catch((err) => {
                Sentry.withScope((scope) => {
                    scope.setFingerprint([url, 'caught error'])
                    Sentry.captureException(err)
                })
                dispatch({
                    type: 'PLACE_ORDER_ERROR',
                    payload: err.response && err.response.data ? [err.response.data] : [],
                })
            })
    }
}

export function placeSubscriptionOrder(
    subscription,
    deliveryAddress,
    email,
    preferredPaymentMethod,
    optinForNews
) {
    const url = `${process.env.CORE_API}/subscription`
    const subscriptionProducts = mapSubscriptionData(subscription)

    return (dispatch, getState) => {
        dispatch({
            type: 'PLACE_SUBSCRIPTION_ORDER',
            payload: { subscription, deliveryAddress, email, preferredPaymentMethod },
        })
        if (
            !validateSubscription(
                {
                    products: subscriptionProducts,
                    deliveryAddress,
                    email,
                    preferredPaymentMethod, // ?? Might not be set, if logged in user chooses stored payment method.
                    optinForNews,
                },
                dispatch,
                getState().user.isLoggedIn
            )
        )
            return

        const validFrom = getDateYyyyMmDd()
        dispatch(
            updateSubscriptionAddress(
                deliveryAddress,
                validFrom,
                () => {
                    axios
                        .post(url, subscriptionProducts)
                        .then((response) => {
                            if (response.data.status === 'failed') {
                                dispatch({
                                    type: 'PLACE_SUBSCRIPTION_ORDER_ERROR',
                                    payload: {
                                        type: 'placeSubscription',
                                        status: response.status,
                                        data: response.data,
                                    },
                                })
                            } else {
                                // Refresh subscription from server before dispatching SUCCESS to update state before any navigation, e.g. back to subscription page
                                dispatch(
                                    fetchSubscription(() => {
                                        dispatch({
                                            type: 'PLACE_SUBSCRIPTION_ORDER_SUCCESS',
                                            payload: response,
                                        })
                                    })
                                )
                            }
                        })
                        .catch((err) => {
                            const status =
                                (err && ((err.response && err.response.status) || err.status)) ||
                                '400'
                            const data =
                                (err && ((err.response && err.response.data) || err.data)) || {}
                            dispatch({
                                type: 'PLACE_SUBSCRIPTION_ORDER_FAILED',
                                payload: { type: 'placeSubscription', status, data },
                            })
                        })
                },
                (err) => {
                    const status =
                        (err && ((err.response && err.response.status) || err.status)) || '400'
                    const data = (err && ((err.response && err.response.data) || err.data)) || {}
                    dispatch({
                        type: 'PLACE_SUBSCRIPTION_ORDER_FAILED',
                        payload: { type: 'placeSubscription', status, data },
                    })
                }
            )
        )
    }
}

export function setDeliveryDate(deliveryDate, setByUser) {
    const capacityNearlyFull = deliveryDate.capacityStatus === 'NEARLY_FULL'

    return (dispatch) => {
        dispatch({
            type: 'SET_DELIVERY_DATE',
            payload: {
                deliveryDate: deliveryDate.date,
                setByUser,
                capacityNearlyFull: capacityNearlyFull,
            },
        })

        dispatch(attemptFetchStock())

        if (capacityNearlyFull && i18n.isInitialized) {
            dispatch(
                showNotification({
                    style: 'INFO',
                    text: i18n.t('capacityNearlyFullAnnouncement', {
                        dateText: getDateAsText(deliveryDate.date, true),
                    }),
                })
            )
        }
    }
}

export function setOptinForNews(optinForNews) {
    return {
        type: 'SET_OPTIN_FOR_NEWS',
        payload: optinForNews,
    }
}

export function checkoutCompleted() {
    return (dispatch) => {
        dispatch({ type: 'CHECKOUT_COMPLETED' })
    }
}
